import React, { useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Stack,
  Typography,
  Button,
  Alert,
} from "@mui/material";
import {
  GroupAdd as GroupAddIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material";
import { GroupTable } from "../components/groups/GroupTable";
import { GroupForm } from "../components/groups/GroupForm";
import { GroupPermissions } from "../components/groups/GroupPermissions";
import { GroupUsers } from "../components/groups/GroupUsers";
import {
  groupsService,
  type Group as GroupType,
} from "@/services/groupsService";
import { permissionService } from "@/services/permissionService";

/**
 * Página de gestión de grupos
 * CRUD completo de grupos con asignación de permisos
 *
 * REGLAS APLICADAS:
 * - Solo <Stack> para layout (NO Grid)
 * - Todos los componentes con size="small"
 * - CRUD completo implementado
 * - Máximo 500 líneas
 * - Usa groupsService con autenticación automática JWT
 */

type Group = GroupType;

interface GroupFormData {
  nombre: string;
  descripcion: string;
  activo: boolean;
}

export function GroupsPage(): React.ReactElement {
  const [refreshKey, setRefreshKey] = useState(0);
  const [groupForm, setGroupForm] = useState<{
    open: boolean;
    group?: Group | null;
  }>({
    open: false,
    group: null,
  });
  const [groupPermissions, setGroupPermissions] = useState<{
    open: boolean;
    group?: Group | null;
  }>({
    open: false,
    group: null,
  });
  const [groupUsers, setGroupUsers] = useState<{
    open: boolean;
    group?: Group | null;
  }>({
    open: false,
    group: null,
  });
  const [actionError, setActionError] = useState<string | null>(null);

  // Funciones para manejar acciones de la tabla
  const handleEdit = (group: Group) => {
    setGroupForm({ open: true, group });
  };

  const handleDelete = async (groupId: string): Promise<void> => {
    try {
      // Usar servicio con autenticación automática JWT
      await groupsService.deleteGroup(groupId);

      // Actualizar la tabla
      setRefreshKey((prev) => prev + 1);
    } catch (error) {
      console.error("Error eliminando grupo:", error);
      throw error; // Re-throw para que GroupTable pueda manejar el error
    }
  };

  const handleAssignPermissions = (group: Group) => {
    setGroupPermissions({ open: true, group });
  };

  const handleViewUsers = (group: Group) => {
    setGroupUsers({ open: true, group });
  };

  const handleToggleStatus = async (
    groupId: string,
    newStatus: boolean,
  ): Promise<void> => {
    try {
      // Usar servicio con autenticación automática JWT
      await groupsService.toggleGroupStatus(groupId, newStatus);

      // Actualizar la tabla
      setRefreshKey((prev) => prev + 1);
    } catch (error) {
      console.error("Error cambiando estado del grupo:", error);
      throw error;
    }
  };

  // Función para crear/editar grupo
  const handleSaveGroup = async (groupData: GroupFormData): Promise<void> => {
    try {
      const isEditing = Boolean(groupForm.group);

      if (isEditing) {
        // Actualizar grupo existente usando servicio con autenticación automática JWT
        await groupsService.updateGroup(groupForm.group!.id, groupData);
      } else {
        // Crear nuevo grupo usando servicio con autenticación automática JWT
        await groupsService.createGroup(groupData);
      }

      // Actualizar la tabla
      setRefreshKey((prev) => prev + 1);
    } catch (error) {
      console.error("Error guardando grupo:", error);
      throw error;
    }
  };

  // Función para asignar permisos
  const handleSaveGroupPermissions = async (
    groupId: string,
    permissionIds: string[],
  ): Promise<void> => {
    try {
      await permissionService.assignPermissionsToGroup(groupId, permissionIds);

      // Actualizar la tabla
      setRefreshKey((prev) => prev + 1);
    } catch (error) {
      console.error("Error asignando permisos:", error);
      throw error;
    }
  };

  const handleCreateNew = () => {
    setGroupForm({ open: true, group: null });
  };

  const handleRefresh = () => {
    setRefreshKey((prev) => prev + 1);
    setActionError(null);
  };

  return (
    <Stack spacing={3}>
      {/* Encabezado */}
      <Box>
        <Typography variant="h4" component="h1" gutterBottom>
          Gestión de Grupos
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Organización de usuarios en grupos con permisos específicos
        </Typography>
      </Box>

      {/* Error de acciones */}
      {actionError && (
        <Alert severity="error" onClose={() => setActionError(null)}>
          {actionError}
        </Alert>
      )}

      {/* Acciones principales */}
      <Stack direction="row" spacing={2}>
        <Button
          variant="contained"
          size="small"
          startIcon={<GroupAddIcon />}
          onClick={handleCreateNew}
        >
          Crear Grupo
        </Button>
        <Button
          variant="outlined"
          size="small"
          startIcon={<RefreshIcon />}
          onClick={handleRefresh}
        >
          Actualizar
        </Button>
      </Stack>

      {/* Tabla de grupos */}
      <Card>
        <CardContent>
          <GroupTable
            key={refreshKey}
            onEdit={handleEdit}
            onDelete={handleDelete}
            onAssignPermissions={handleAssignPermissions}
            onViewUsers={handleViewUsers}
            onToggleStatus={handleToggleStatus}
          />
        </CardContent>
      </Card>

      {/* Modal de formulario de grupo */}
      <GroupForm
        open={groupForm.open}
        onClose={() => setGroupForm({ open: false, group: null })}
        onSave={handleSaveGroup}
        group={groupForm.group || null}
      />

      {/* Modal de asignación de permisos */}
      <GroupPermissions
        open={groupPermissions.open}
        onClose={() => setGroupPermissions({ open: false, group: null })}
        onSave={handleSaveGroupPermissions}
        group={groupPermissions.group || null}
      />

      {/* Modal de usuarios del grupo */}
      <GroupUsers
        open={groupUsers.open}
        onClose={() => setGroupUsers({ open: false, group: null })}
        group={groupUsers.group || null}
      />
    </Stack>
  );
}
